// src/components/main/ExternalLessonClicks.js
import React, { useEffect, useMemo, useState } from 'react';
import { getAllExternalLessons } from '../apicalls/externalLessons';
import { getAllExternalLessonClicks, getClicksForLesson } from '../apicalls/externalLessonClicks';
import { isAuthenticated } from '../apicalls/auth';

const clamp = (v, n = 30) => (typeof v === 'string' ? (v.length > n ? v.slice(0, n) + '…' : v) : '');
const safe = (v, fb = '—') => (v === null || v === undefined || v === '' ? fb : v);
const lessonRowsFromResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};
const lessonListParams = {
  page: 1,
  per_page: 20,
  sort_by: 'start_date_time',
  sort_dir: 'desc',
};
const recentClicksParams = {
  page: 1,
  per_page: 100,
};
const domainFrom = (url) => {
  try { return new URL(url).hostname.replace('www.', ''); } catch { return null; }
};

const ExternalLessonClicks = () => {
  const [lessons, setLessons] = useState([]);
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('start_desc'); // start_desc | start_asc | title
  const [recentClicks, setRecentClicks] = useState([]);
  const [recentClickTotal, setRecentClickTotal] = useState(0);
  const [clicks, setClicks] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loadingLessons, setLoadingLessons] = useState(true);
  const [loadingRecentClicks, setLoadingRecentClicks] = useState(true);
  const [loadingClicks, setLoadingClicks] = useState(false);
  const { access_token } = isAuthenticated();

  useEffect(() => {
    const fetchLessons = async () => {
      if (!access_token) return;
      setLoadingLessons(true);
      try {
        const data = await getAllExternalLessons(access_token, lessonListParams);
        setLessons(lessonRowsFromResponse(data));
      } catch (e) {
        console.error('Error fetching external lessons:', e);
        setLessons([]);
      } finally {
        setLoadingLessons(false);
      }
    };
    fetchLessons();
  }, [access_token]);

  useEffect(() => {
    const fetchRecentClicks = async () => {
      if (!access_token) return;
      setLoadingRecentClicks(true);
      try {
        const data = await getAllExternalLessonClicks(access_token, recentClicksParams);
        setRecentClicks(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);
        setRecentClickTotal(data?.pagination?.total || 0);
      } catch (e) {
        console.error('Error fetching recent external lesson clicks:', e);
        setRecentClicks([]);
        setRecentClickTotal(0);
      } finally {
        setLoadingRecentClicks(false);
      }
    };
    fetchRecentClicks();
  }, [access_token]);

  const sortedFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = !q
      ? lessons
      : lessons.filter((l) => {
          const hay =
            `${safe(l.title)} ${safe(l.location)} ${safe(l.metadata?.full_name)} ${safe(l.external_url)}`
              .toLowerCase();
          return hay.includes(q);
        });

    const arr = [...filtered];
    if (sortBy === 'start_desc') {
      arr.sort((a, b) => new Date(b.start_date_time) - new Date(a.start_date_time));
    } else if (sortBy === 'start_asc') {
      arr.sort((a, b) => new Date(a.start_date_time) - new Date(b.start_date_time));
    } else {
      arr.sort((a, b) => String(a.title).localeCompare(String(b.title)));
    }
    return arr;
  }, [lessons, query, sortBy]);

  const loadClicks = async (lesson) => {
    if (!access_token || !lesson?.id) return;
    setSelectedLesson(lesson);
    setLoadingClicks(true);
    setClicks([]);
    try {
      const data = await getClicksForLesson(lesson.id, access_token);
      setClicks(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(`Error fetching clicks for lesson ${lesson.id}:`, e);
      setClicks([]);
    } finally {
      setLoadingClicks(false);
    }
  };

  const lessonFromRecentClick = (click) => {
    const existingLesson = lessons.find((lesson) => String(lesson.id) === String(click.external_lesson_id));
    if (existingLesson) return existingLesson;
    return {
      id: click.external_lesson_id,
      title: click.lesson_title || `External lesson ${click.external_lesson_id}`,
      start_date_time: click.lesson_start_date_time,
      end_date_time: click.lesson_end_date_time,
      location: click.lesson_location,
      external_url: click.lesson_external_url,
      level: click.lesson_level,
      metadata: {
        full_name: click.lesson_provider || click.lesson_coach_name,
        coach_name: click.lesson_coach_name || click.lesson_provider,
      },
    };
  };

  const fmtDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
      : '—';
  const fmtDT = (iso) =>
    iso
      ? new Date(iso).toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  const renderClicksTable = (rows, { showLesson = false, onClickRow } = {}) => (
    <div className="table-responsive" style={{ maxHeight: 'calc(80vh - 140px)', overflowY: 'auto' }}>
      <table className="table table-sm table-striped table-hover align-middle">
        <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
          <tr>
            <th style={{ minWidth: 160 }}>Clicked At</th>
            {showLesson ? <th style={{ minWidth: 260 }}>Lesson</th> : null}
            {showLesson ? <th style={{ minWidth: 140 }}>Provider</th> : null}
            <th style={{ minWidth: 90 }}>User ID</th>
            <th style={{ minWidth: 120 }}>IP</th>
            <th style={{ minWidth: 120 }}>Country</th>
            <th style={{ minWidth: 120 }}>Device</th>
            <th style={{ minWidth: 260 }}>User Agent</th>
            <th style={{ minWidth: 220 }}>Referrer</th>
            <th style={{ minWidth: 130 }}>UTM Src</th>
            <th style={{ minWidth: 130 }}>UTM Med</th>
            <th style={{ minWidth: 150 }}>UTM Camp</th>
            <th style={{ minWidth: 180 }}>Session ID</th>
            <th style={{ minWidth: 260 }}>Custom Meta</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr
              key={c.id}
              onClick={onClickRow ? () => onClickRow(c) : undefined}
              role={onClickRow ? 'button' : undefined}
              tabIndex={onClickRow ? 0 : undefined}
              onKeyDown={onClickRow ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onClickRow(c);
                }
              } : undefined}
              style={onClickRow ? { cursor: 'pointer' } : undefined}
              title={onClickRow ? `Open external lesson ${c.external_lesson_id}` : undefined}
              aria-label={onClickRow ? `Open external lesson ${c.external_lesson_id}: ${c.lesson_title || ''}` : undefined}
            >
              <td>{fmtDT(c.clicked_at)}</td>
              {showLesson ? (
                <td>
                  <div className="fw-semibold">{safe(c.lesson_title || c.external_lesson_id)}</div>
                  <div className="text-muted small">
                    ID: {safe(c.external_lesson_id)} · {fmtDT(c.lesson_start_date_time)}
                  </div>
                </td>
              ) : null}
              {showLesson ? (
                <td>{safe(c.lesson_provider || c.lesson_coach_name, '—')}</td>
              ) : null}
              <td>{safe(c.user_id, '—')}</td>
              <td>{safe(c.ip_address, '—')}</td>
              <td>{safe(c.country, '—')}</td>
              <td>{safe(c.device_type, '—')}</td>
              <td className="text-truncate" title={c.user_agent}>
                {safe(c.user_agent, '—')}
              </td>
              <td className="text-truncate" title={c.referrer_url}>
                {safe(c.referrer_url, '—')}
              </td>
              <td>{safe(c.utm_source, '—')}</td>
              <td>{safe(c.utm_medium, '—')}</td>
              <td className="text-truncate" title={c.utm_campaign}>
                {safe(c.utm_campaign, '—')}
              </td>
              <td className="text-truncate" title={c.session_id}>
                {safe(c.session_id, '—')}
              </td>
              <td>
                {c?.metadata && Object.keys(c.metadata).length ? (
                  <pre
                    className="mb-0 small"
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 120, overflowY: 'auto' }}
                    title={JSON.stringify(c.metadata)}
                  >
                    {JSON.stringify(c.metadata, null, 2)}
                  </pre>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <main className="container-fluid my-4">
      <h2 className="mb-3">External Lesson Click Tracking</h2>

      <div className="d-flex gap-3" style={{ minHeight: '70vh' }}>
        {/* LEFT LIST PANEL */}
        <aside
          className="bg-white border rounded-3 p-3 d-flex flex-column"
          style={{ width: 360, maxWidth: '33vw', minWidth: 320}}
        >
          <div className="d-flex align-items-center gap-2 mb-2">
            <h5 className="m-0">Lessons</h5>
            <span className="badge bg-secondary">{lessons.length}</span>
          </div>

          <div className="d-flex gap-2 mb-2">
            <input
              className="form-control"
              placeholder="Search title, location, provider, URL…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select
              className="form-select"
              style={{ maxWidth: 150 }}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label="Sort lessons"
            >
              <option value="start_desc">Newest</option>
              <option value="start_asc">Oldest</option>
              <option value="title">Title A–Z</option>
            </select>
          </div>

          {loadingLessons ? (
            <div className="text-muted small">Loading lessons…</div>
          ) : sortedFiltered.length === 0 ? (
            <div className="text-muted small">No lessons match your filters.</div>
          ) : (
            <div className="list-group overflow-auto flex-grow-1">
              {sortedFiltered.map((l) => {
                const provider = l?.metadata?.full_name || domainFrom(l?.external_url) || 'Unknown';
                const isActive = selectedLesson?.id === l.id;
                const subtitle =
                  safe(l.location) && !String(l.location).startsWith('http')
                    ? clamp(l.location, 44)
                    : domainFrom(l.external_url) || clamp(l.external_url, 44);

                return (
                  <button
                    key={l.id}
                    type="button"
                    className={
                      'list-group-item list-group-item-action py-2 ' + (isActive ? 'active' : '')
                    }
                    onClick={() => loadClicks(l)}
                    disabled={loadingClicks && isActive}
                    title={l.title}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="me-2 text-start">
                        <div className="fw-semibold">{clamp(l.title, 38)}</div>
                        <div className={isActive ? 'text-white-50 small' : 'text-muted small'}>
                          {fmtDate(l.start_date_time)} · {subtitle}
                        </div>
                      </div>
                      <span
                        className={'badge rounded-pill ' + (isActive ? 'bg-light text-dark' : 'bg-light')}
                        title={provider}
                      >
                        {clamp(provider, 14)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </aside>

        {/* RIGHT DETAILS PANEL */}
        <section className="flex-grow-1 d-flex flex-column">
          {!selectedLesson ? (
            <>
              <div className="d-flex flex-wrap align-items-baseline gap-2 mb-2">
                <h5 className="m-0">Recent Clicks</h5>
                <small className="text-muted">
                  {recentClickTotal ? `${recentClicks.length} shown · ${recentClickTotal} total` : 'Most recent first'}
                </small>
              </div>
              {loadingRecentClicks ? (
                <div className="text-muted">Loading recent clicks…</div>
              ) : recentClicks.length === 0 ? (
                <div className="text-muted">No clicks recorded yet.</div>
              ) : (
                renderClicksTable(recentClicks, {
                  showLesson: true,
                  onClickRow: (click) => loadClicks(lessonFromRecentClick(click)),
                })
              )}
            </>
          ) : (
            <>
              <div className="d-flex flex-wrap align-items-baseline gap-2 mb-2">
                <h5 className="m-0">{selectedLesson.title}</h5>
                <span className="text-muted small">
                  (ID: {selectedLesson.id} · {fmtDT(selectedLesson.start_date_time)} –{' '}
                  {fmtDT(selectedLesson.end_date_time)})
                </span>
              </div>
              <div className="mb-3 small">
                <div className="text-truncate" title={selectedLesson.external_url}>
                  URL:{' '}
                  <a href={selectedLesson.external_url} target="_blank" rel="noopener noreferrer">
                    {selectedLesson.external_url}
                  </a>
                </div>
                <div className="text-truncate" title={selectedLesson.location}>
                  Location: {safe(selectedLesson.location)}
                </div>
              </div>

              {loadingClicks ? (
                <div className="text-muted">Loading clicks…</div>
              ) : clicks.length === 0 ? (
                <div className="text-muted">No clicks recorded for this lesson yet.</div>
              ) : (
                <>
                  <div className="mb-2">
                    <small className="text-muted">Total Clicks: {clicks.length}</small>
                  </div>

                  {renderClicksTable(clicks)}
                </>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
};

export default ExternalLessonClicks;
